import React, { useState, useEffect, useCallback, useMemo } from "react";
import useApi from "@/hooks/useApi";

interface ScrubberEvent {
  id: number;
  conversationId: string;
  patternName: string;
  isBuiltIn: boolean;
  direction: string;
  messageHint?: string;
  createdAt: string;
}

interface EventsResponse {
  events: ScrubberEvent[];
  total: number;
  page: number;
  pageSize: number;
}

type SortKey = "createdAt" | "patternName" | "direction";
type SortDir = "asc" | "desc";

// Optional: link to the conversation in the messaging console.
// Update this base URL if/when the messaging app routing is known.
const CONVERSATION_BASE_URL = "";

export default function ScrubberReport() {
  const api = useApi();
  const [events, setEvents] = useState<ScrubberEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPattern, setFilterPattern] = useState("");
  const [filterDirection, setFilterDirection] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [copied, setCopied] = useState<number | null>(null);
  const pageSize = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(pageSize),
      });
      if (filterPattern) params.set("patternName", filterPattern);
      if (filterDirection !== "all") params.set("direction", filterDirection);

      const res = await api.admin.get(`Scrubber/events?${params}`);
      if (!res.ok) { setLoading(false); return; }
      const data: EventsResponse = await res.json();
      setEvents(Array.isArray(data?.events) ? data.events : []);
      setTotal(data?.total ?? 0);
    } catch {
      // safe default
    } finally {
      setLoading(false);
    }
  }, [api.admin, currentPage, filterPattern, filterDirection]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredEvents = useMemo(() => {
    let result = [...events];
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (e) =>
          (e.conversationId ?? "").toLowerCase().includes(lower) ||
          (e.patternName ?? "").toLowerCase().includes(lower) ||
          (e.messageHint ?? "").toLowerCase().includes(lower)
      );
    }
    result.sort((a, b) => {
      let aVal: any = a[sortKey];
      let bVal: any = b[sortKey];
      if (sortKey === "createdAt") { aVal = new Date(aVal).getTime(); bVal = new Date(bVal).getTime(); }
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [events, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sortArrow = (key: SortKey) => sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const copyConversationId = async (id: string, eventId: number) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(eventId);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // clipboard not available
    }
  };

  const directionBadge = (dir: string) => (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
      dir === "agent" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
    }`}>
      {dir === "agent" ? "Agent" : "Visitor"}
    </span>
  );

  const sourceBadge = (isBuiltIn: boolean) => (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
      isBuiltIn ? "bg-slate-100 text-slate-600" : "bg-cornflower-blue-50 text-cornflower-blue-700"
    }`}>
      {isBuiltIn ? "Built-in" : "Custom"}
    </span>
  );

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-base font-semibold text-slate-800">Scrubber Report</h3>
        <p className="text-sm text-slate-500 mt-0.5">
          Each row is a message that triggered a scrubber rule. No sensitive values are stored —
          only the rule name, conversation ID, and a safe hint to help diagnose false positives.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          placeholder="Search conversation, rule, hint..."
          className="px-3 py-1.5 border border-slate-300 rounded text-sm w-64 focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search scrubber events"
        />
        <input
          type="text"
          placeholder="Filter by rule name..."
          className="px-3 py-1.5 border border-slate-300 rounded text-sm w-44 focus-visible:outline-2 focus-visible:outline-cornflower-blue-500"
          value={filterPattern}
          onChange={(e) => { setFilterPattern(e.target.value); setCurrentPage(1); }}
          aria-label="Filter by pattern name"
        />
        <label htmlFor="filterDir" className="sr-only">Filter by direction</label>
        <select
          id="filterDir"
          className="px-3 py-1.5 border border-slate-300 rounded text-sm"
          value={filterDirection}
          onChange={(e) => { setFilterDirection(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">All Directions</option>
          <option value="visitor">Visitor</option>
          <option value="agent">Agent</option>
        </select>
        <button
          className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
          onClick={() => { setSearch(""); setFilterPattern(""); setFilterDirection("all"); setCurrentPage(1); }}
        >
          Clear
        </button>
        <span className="text-xs text-slate-400 ml-auto">{total} total events</span>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("createdAt")}>Date/Time{sortArrow("createdAt")}</th>
              <th className="px-3 py-2">Conversation ID</th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("patternName")}>Rule Triggered{sortArrow("patternName")}</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2 cursor-pointer" onClick={() => handleSort("direction")}>Direction{sortArrow("direction")}</th>
              <th className="px-3 py-2">Hint</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Loading...</td></tr>
            ) : filteredEvents.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center">
                  <p className="text-slate-400 text-sm">No scrubber events recorded yet.</p>
                  <p className="text-slate-300 text-xs mt-1">
                    Events appear here when the scrubbing engine redacts a message.
                  </p>
                </td>
              </tr>
            ) : filteredEvents.map((ev) => (
              <tr key={ev.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-1.5 text-xs whitespace-nowrap">{new Date(ev.createdAt).toLocaleString()}</td>
                <td className="px-3 py-1.5 text-xs font-mono">
                  <div className="flex items-center gap-1.5">
                    {CONVERSATION_BASE_URL ? (
                      <a
                        href={`${CONVERSATION_BASE_URL}/${ev.conversationId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cornflower-blue-600 hover:underline truncate max-w-[120px]"
                        title={ev.conversationId}
                      >
                        {ev.conversationId}
                      </a>
                    ) : (
                      <span className="truncate max-w-[120px]" title={ev.conversationId}>
                        {ev.conversationId || "—"}
                      </span>
                    )}
                    {ev.conversationId && (
                      <button
                        onClick={() => copyConversationId(ev.conversationId, ev.id)}
                        className="shrink-0 text-slate-400 hover:text-slate-600"
                        aria-label="Copy conversation ID"
                        title="Copy conversation ID"
                      >
                        {copied === ev.id ? (
                          <svg className="w-3.5 h-3.5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5 text-xs font-medium text-slate-700">{ev.patternName}</td>
                <td className="px-3 py-1.5">{sourceBadge(ev.isBuiltIn)}</td>
                <td className="px-3 py-1.5">{directionBadge(ev.direction)}</td>
                <td className="px-3 py-1.5 text-xs text-slate-500 max-w-[240px] truncate" title={ev.messageHint ?? ""}>
                  {ev.messageHint || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm text-slate-500">
          <span>Page {currentPage} of {totalPages}</span>
          <div className="flex gap-1">
            <button className="px-2 py-1 rounded border" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>First</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Prev</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
            <button className="px-2 py-1 rounded border" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>Last</button>
          </div>
        </div>
      )}
    </div>
  );
}
